<template>
  <div class="location-editor location-section">
    <div class="address-details d-grid gap-3">
      <div class="row">
        <div class="col-md-12">
          <div :class="{ 'is-invalid': showValidationErrors || hasInvalidLocationType }">
            <label for="venue-field">
              {{ venueLabel }}
            </label>
            <div v-if="hasInvalidLocationType || hasGenericError" class="is-invalid">
              <span class="invalid-feedback d-block">{{ i18n.t('components.location_editor.invalid_location') }}</span>
            </div>
            <!-- We use v-show instead of v-if here so that the component is only hidden, not destroyed,
                 when a location is set. This ensures that if a location is selected from the dropdown, and the
                 Google Places Service query finishes after the dropdown has already been hidden, we'll still receive
                 the @place-changed event with the selected place's details. -->
            <custom-gmap-autocomplete v-show="showAutoComplete" id="location-query-field" ref="autocomplete"
                                      :value="locationQuery"
                                      :allow-enter-manually-option="true"
                                      :class="['form-control query-input', {'is-invalid': showValidationErrors, [gmapAutocompleteClass]: true}]"
                                      :name="getFullFieldName('venue')" required
                                      :input-placeholder="autocompletePlaceholder"
                                      :options="autocompleteOptions"
                                      :select-first-on-enter="true"
                                      @blur="locationQueryBlurred"
                                      @input="setCurrentAutocompleteQuery"
                                      @place-changed="handleAutocompletePlaceChanged"
                                      @enter-manually-selected="enterManuallySelected" />
            <input v-if="!showAutoComplete" id="venue-field" v-model="location.venue" type="text" class="form-control"
                   :name="getFullFieldName('venue')" required @blur="changeAttribute">
            <validation-errors v-if="showValidationErrors" :errors="validationErrorsAsArray" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-7 d-grid gap-3">
          <template v-if="showAddressFields">
            <div class="row">
              <div class="col-md-12">
                <label>{{ i18n.t('components.location_editor.street_address') }}</label>
              </div>
            </div>
            <div class="row">
              <input-group :has-error="location.geocode_failed" col-width-class="col-4">
                <input-field v-bind="propsForLocationField('street_number')" @changed="addressComponentChanged" />
              </input-group>

              <input-group :has-error="location.geocode_failed" col-width-class="col-8">
                <input-field v-bind="propsForLocationField('street')" @changed="addressComponentChanged" />
              </input-group>
            </div>
            <div class="row">
              <input-group :has-error="location.geocode_failed" col-width-class="col-12">
                <input-field v-bind="propsForLocationField('locality')" @changed="addressComponentChanged" />
              </input-group>
            </div>
            <div v-if="displayRegion" class="row">
              <input-group :has-error="location.geocode_failed" col-width-class="col-12">
                <input-field v-bind="propsForLocationField('region')" @changed="addressComponentChanged" />
              </input-group>
            </div>
            <div class="row gy-3">
              <input-group :has-error="location.geocode_failed" col-width-class="col-md-6 col-12">
                <input-field v-bind="propsForLocationField('postal_code')" @changed="addressComponentChanged" />
              </input-group>
              <input-group :has-error="location.geocode_failed" col-width-class="col-md-6 col-12">
                <selector-input field="location[country]"
                                :options="countryOptions"
                                :initial-value="location.country"
                                :blank-if-no-selection="true"
                                @changed="countryChanged" />
              </input-group>
            </div>
          </template>
          <div v-if="location.latitude !== null">
            <div>
              <input v-model="location.query" type="hidden" :name="getFullFieldName('query')">
              <input v-model="location.longitude" type="hidden" :name="getFullFieldName('longitude')" required>
              <input v-model="location.latitude" type="hidden" :name="getFullFieldName('latitude')" required>
            </div>
          </div>
        </div>
        <div class="col-md-5">
          <static-map v-if="showAddressFields" class="address-details-map" :location="location" />
        </div>
      </div>
    </div>
    <div class="inline-links">
      <div class="local-event-buttons">
        <template v-if="showAddressFields">
          <button type="button" class="btn btn-link unpadded" @click="reset">
            {{ i18n.t('components.location_editor.reset') }}
          </button>
          <button v-if="showExtraInfoLink" type="button" class="btn btn-link unpadded enter-more-details" @click="editExtraInfo" @input="changeAttribute">
            {{ i18n.t('components.location_editor.show_extra') }}
          </button>
        </template>
      </div>
    </div>

    <div v-show="showExtraInfo" class="mt10">
      <label for="extra_location_info">
        {{ i18n.t('components.location_editor.additional_details') }}
      </label>
      <textarea v-model="location.extra_location_info"
                class="form-control"
                :name="getFullFieldName('extra_location_info', parentEntityNamespace)"
                @input="changeAttribute" />
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import intersection from 'lodash/intersection'
import isPresent from '../../helpers/is-present'

import CustomGmapAutocomplete from './custom-gmap-autocomplete.vue'
import SelectorInput from '../inputs/selector-input'

import GeocodeLocation from '../../mixins/geocode-location'
import LocationEditFromGoogleMapsAutocomplete from './mixins/location-edit-from-google-maps-autocomplete'

import ValidationErrors from './validation-errors'
import InputField from './location-editor/input-field'
import InputGroup from './location-editor/input-group'
import StaticMap from './location-editor/static-map'

const DEFAULT_LOCATION = {query: null,
                          street_number: null,
                          street: null,
                          postal_code: null,
                          country: null,
                          locality: null,
                          region: null,
                          latitude: null,
                          longitude: null,
                          extra_location_info: null
}

export default {
  components: { CustomGmapAutocomplete, InputField, InputGroup, SelectorInput, StaticMap, ValidationErrors },

  mixins: [ GeocodeLocation, LocationEditFromGoogleMapsAutocomplete ],

  props: {
    autocompletePlaceholder: {
      type: String,
      required: false
    },
    countryOptions: {
      type: Array,
      required: true
    },
    displayRegion: {
      type: Boolean,
      default: false
    },
    gmapAutocompleteClass: {
      type: String,
      default: ''
    },
    initialValue: {
      type: Object,
      default: () => ({
        'geocode_failed': false,
        'errors': {},
        ...DEFAULT_LOCATION
      })
    },
    locationObjectNamespace: {
      // namespace for location-related form elements
      type: String,
      default: 'location',
      required: false
    },
    parentEntityNamespace: {
      // namespace for form elements related to the parent entity of the location (e.g., 'event')
      type: String,
      default: '',
      required: false
    },
    supportsExtraInfo: {
      type: Boolean,
      default: false
    },
    validAddressTypes: {
      type: Array,
      default: () => ['street_address', 'point_of_interest']
    },
    validationErrors: {
      type: Object,
      required: false,
      default: () => ({})
    },
    venueLabel: {
      type: String,
      required: false,
      default() { return inject('i18n').t('components.location_editor.venue_label') }
    }
  },

  emits: ['changed'],

  data() {
    // Clone object received as prop to avoid updating from this child component
    let location = Object.assign({}, DEFAULT_LOCATION, this.initialValue, {geocoding: false})
    if (!this.excludeVenueField) {
      location.venue = this.initialValue?.venue
    }

    return {
      currentQuery: null,
      locationTypes: [],
      editingExtraInfo: false,
      enterAddressManually: false,
      location: location
    }
  },
  computed: {
    boundaryBox() {
      return this.agraConfiguration.locationSearchBias.boundaryBox
    },
    country() {
      return this.agraConfiguration.organisationCountry
    },
    extraInfoPresent() {
      return isPresent(this.location.extra_location_info)
    },
    showExtraInfoLink() {
      return this.supportsExtraInfo && !this.extraInfoPresent && !this.editingExtraInfo
    },
    showExtraInfo() {
      return this.supportsExtraInfo && (this.extraInfoPresent || this.editingExtraInfo)
    },
    hasGenericError() {
      return this.validationErrors.base && this.validationErrors.base.length > 0
    },
    hasInvalidLocationType() {
      return this.locationTypes.length && intersection(this.locationTypes, this.validAddressTypes).length === 0
    },
    showAutoComplete() {
      return !this.enterAddressManually && !this.location.query
    },
    locationQuery() {
      // If there is a location loaded already, for eg from inititalLocation use its query. Otherwise, use
      // whatever the user has typed into the field
      if (this.currentQuery) {
        return this.currentQuery
      } else if (this.location && this.location.query ) {
        return this.location.query
      } else {
        return ''
      }
    },
    showAddressFields() {
      // show fields if the values were autocompleted or if user elects to enter manually
      return this.enterAddressManually || this.location.query || this.location.geocoding
    },
    searchTextForPlaceGeocoding() {
      return `${this.location.street_number||''} ${this.location.street||''} ${this.location.locality||''} ${this.location.region||''} ${this.location.postal_code||''} ${this.location.country||''}`
    },
    showValidationErrors() {
      return this.validationErrors && Object.keys(this.validationErrors).length > 0
    },
    validationErrorsAsArray() {
      let errorsArray = []

      Object.keys(this.validationErrors).forEach((field) => {
        // Don't include "base" error, which is handled by hasGenericError instead
        if (field !== 'base') {
          this.validationErrors[field].forEach((error) => errorsArray.push(error))
        }
      })

      return errorsArray
    }
  },

  methods: {
    addressComponentChanged() {
      this.triggerGeocodingIfAddressReady()
      this.changeAttribute()
    },
    countryChanged(countryCode) {
      this.location.country = countryCode
      this.addressComponentChanged()
    },
    changeAttribute() {
      this.$emit('changed', this.location)
      this.enterAddressManually = true
    },
    editExtraInfo() {
      this.editingExtraInfo = true
    },
    enterManuallySelected: async function(query) {
      if (this.blurGeocodingPromise) {
        await this.blurGeocodingPromise
        this.blurGeocodingPromise = null
      }

      this.reset()
      this.enterAddressManually = true

      this.location.query = query
      this.location.venue = query
    },

    handleAutocompletePlaceChanged: async function(place) {
      if (this.blurGeocodingPromise) {
        await this.blurGeocodingPromise
        this.blurGeocodingPromise = null
      }
      this.placeChanged(place)
    },

    locationQueryBlurred() {
      // Automatically geocode the location if the user entered something on the query input but
      // didn't select an option from the autocomplete results
      if (isPresent(this.currentQuery) && (!this.location || !this.location.latitude) && !this.enterAddressManually) {
        this.blurGeocodingPromise = new Promise((resolve) => {
          this.geocodeLocation(() => {
            if (this.geocodeSucceeded) {
              this.$emit('changed', this.location)
            }
            resolve(true)
          })
        })
      }
    },

    reset() {
      this.location.geocode_failed = false
      this.location.geocoding = false
      this.location.query = null
      this.location.venue = null
      this.location.street_number = null
      this.location.street = null
      this.location.postal_code = null
      this.location.country = null
      this.location.locality = null
      this.location.region = null
      this.location.latitude = null
      this.location.longitude = null
      this.location.extra_location_info = ''
      this.locationTypes = []
      this.currentQuery = null
      this.changeAttribute()
      this.editingExtraInfo = false
      this.enterAddressManually = false
      this.$refs.autocomplete.clearQuery()
    },

    setCurrentAutocompleteQuery(newQuery) {
      this.currentQuery = newQuery
    },

    getFullFieldName(name, locationObjectNamespace) {
      locationObjectNamespace = locationObjectNamespace || this.locationObjectNamespace
      return locationObjectNamespace ? `${locationObjectNamespace}[${name}]` : name
    },
    propsForLocationField(fieldName) {
      return {
        error: (this.location.errors && this.location.errors[fieldName]) || '',
        fieldName,
        fullFieldName: this.locationObjectNamespace? `${this.locationObjectNamespace}[${fieldName}]`: fieldName,
        initialLocation: this.location
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @use "~common_styles/breakpoints" as breakpoints;

  .inline-links {
    @include breakpoints.screen-md-up {
      display: flex;
      justify-content: space-between;
    }
  }

  /* Wrap long text in this button */
  button.enter-more-details {
    white-space: normal;
    text-align: left;
  }

  .btn-link.unpadded {
    padding-left: 0;
    padding-right: 0;
  }
</style>
